import clsx from 'clsx';
import { GripVertical, MapPin, Droplet } from 'lucide-react';
import { formatCspireAssignmentDisplayName } from '../../../utils/cspireDisplay';

const getClientDisplayName = (client) => {
  if (!client) return 'Unknown Customer';
  if (client.isCommercial && client.companyName) return client.companyName;
  const lastName = client.contactLastName?.trim();
  const firstName = client.contactFirstName?.trim();
  if (lastName && firstName) return `${lastName}, ${firstName}`;
  return lastName || firstName || client.companyName || 'Unknown Customer';
};

export default function AssignmentCard({
  assignment,
  onDragStart,
  onDragEnd,
  onClick,
  isDragging,
  compact = false,
}) {
  const client = assignment.client;
  const cspireWorkOrder = assignment.cspireWorkOrder;
  const franklinSite = assignment.franklinSite;
  const isCspire = assignment.isCspire && cspireWorkOrder;
  const isFranklin = !!franklinSite || assignment.isFranklin;
  const isPriority = assignment.isPriority === true;
  const currentMonth = new Date().getMonth() + 1;
  const isOilChangeMonth = client && client.oilChangeMonth === currentMonth;

  const displayName = isFranklin
    ? `Franklin - ${franklinSite?.locationName || franklinSite?.siteId || 'Site'}`
    : isCspire
      ? formatCspireAssignmentDisplayName(cspireWorkOrder)
      : getClientDisplayName(client);

  const location = isFranklin
    ? ([franklinSite?.city, franklinSite?.state].filter(Boolean).join(', ') || 'Franklin Site')
    : isCspire
      ? (cspireWorkOrder.site?.city && cspireWorkOrder.site?.state
        ? `${cspireWorkOrder.site.city}, ${cspireWorkOrder.site.state}`
        : (cspireWorkOrder.locationName || cspireWorkOrder.site?.locationName || 'CSpire Site'))
      : client ? `${client.city}, ${client.state}` : '';

  const isDraggable = !!client || isCspire || isFranklin;

  return (
    <div
      draggable={isDraggable}
      onDragStart={(e) => {
        if (!isDraggable) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', assignment.id);
        onDragStart?.(assignment);
      }}
      onDragEnd={onDragEnd}
      onClick={() => onClick?.(assignment)}
      className={clsx(
        'hub-card-draggable',
        compact ? 'py-1' : '',
        isDragging && 'opacity-50',
        isPriority ? 'border-red-300 bg-red-50/30' : ''
      )}
    >
      <GripVertical className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="font-medium text-gray-900 truncate text-xs">{displayName}</span>
          {isCspire && <span className="text-2xs px-1 rounded bg-purple-100 text-purple-800">CSpire</span>}
          {isFranklin && <span className="text-2xs px-1 rounded bg-emerald-100 text-emerald-800">Franklin</span>}
          {isPriority && <span className="text-2xs px-1 rounded bg-red-100 text-red-700">Repair</span>}
          {isOilChangeMonth && (
            <span className="text-2xs px-1 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-0.5">
              <Droplet className="h-2.5 w-2.5" />Oil
            </span>
          )}
        </div>
        {location && (
          <div className="flex items-center gap-1 text-2xs text-gray-500 mt-0.5">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{location}</span>
          </div>
        )}
      </div>
    </div>
  );
}
